import React, { useState, useEffect, useRef } from 'react';
import { CanvasComponent } from './components/Canvas/CanvasComponent';
import { Toolbar } from './components/Canvas/Toolbar';
import { 
  BackgroundType, 
  ToolType, 
  DrawingState, 
  UIState,
  Line,
  TextNote,
  LineStyle
} from './components/Canvas/types';
import { HistoryManager } from './components/UndoRedo/HistoryManager';
import './styles.css';

function App() {
  const [backgroundType, setBackgroundType] = useState<BackgroundType>('grid');
  
  const [drawingState, setDrawingState] = useState<DrawingState>({ 
    lines: [],
    textNotes: []
  });
  
  const [uiState, setUiState] = useState<UIState>({
    offsetX: 0,
    offsetY: 0,
    scale: 1,
    tool: 'pointer',
    penColor: '#000000',
    penWidth: 3,
    eraserSize: 30,
    lineStyle: 'solid',
    selection: [],
    isSelecting: false,
    isDraggingSelection: false
  });
  
  const historyManagerRef = useRef<HistoryManager | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  
  useEffect(() => {
    historyManagerRef.current = new HistoryManager(drawingState);
    setCanUndo(false);
    setCanRedo(false);
  }, []);
  
  const handleToolChange = (tool: ToolType) => {
    setUiState(prev => ({ ...prev, tool }));
  };
  
  const handleColorChange = (color: string) => {
    setUiState(prev => ({ ...prev, penColor: color }));
  };
  
  const handleWidthChange = (width: number) => {
    setUiState(prev => ({ ...prev, penWidth: width }));
  };
  
  const handleLineStyleChange = (style: LineStyle) => {
    if (uiState.selection.length > 0) {
      handleLineStylesChange(style);
    } else {
      setUiState(prev => ({ ...prev, lineStyle: style }));
    }
  };
  
  const handleLineStylesChange = (style: LineStyle) => {
    const newLines = drawingState.lines.map(line => 
      uiState.selection.includes(line.id) ? { ...line, lineStyle: style } : line
    );
    
    handleUpdateLines(newLines, drawingState.textNotes);
    setUiState(prev => ({ ...prev, lineStyle: style }));
  };
  
  const handleBackgroundChange = (type: BackgroundType) => {
    setBackgroundType(type);
  };
  
  const handleUiStateChange = (newUiState: UIState) => {
    setUiState(newUiState);
  };
  
  const handleAddLine = (line: Line) => {
    const newDrawingState = {
      ...drawingState,
      lines: [...drawingState.lines, line]
    };
    
    setDrawingState(newDrawingState);
    
    if (historyManagerRef.current) {
      historyManagerRef.current.push(newDrawingState);
      setCanUndo(true);
      setCanRedo(false);
    }
  };
  
  const handleDeleteLine = (lineId: string) => {
    const newDrawingState = {
      ...drawingState,
      lines: drawingState.lines.filter(line => line.id !== lineId)
    };
    
    setDrawingState(newDrawingState);
    
    if (historyManagerRef.current) {
      historyManagerRef.current.push(newDrawingState);
      setCanUndo(true);
      setCanRedo(false);
    }
  };
  
  const handleUpdateLines = (lines: Line[], textNotes?: TextNote[]) => {
    const newDrawingState = {
      lines: [...lines],
      textNotes: textNotes || drawingState.textNotes || []
    };
    
    setDrawingState(newDrawingState);
    
    if (historyManagerRef.current) {
      historyManagerRef.current.push(newDrawingState);
      setCanUndo(true);
      setCanRedo(false);
    }
  };
  
  const handleLineColorChange = (color: string) => {
    const newLines = drawingState.lines.map(line => 
      uiState.selection.includes(line.id) ? { ...line, color } : line
    );
    
    const newTextNotes = (drawingState.textNotes || []).map(note => 
      uiState.selection.includes(note.id) ? { ...note, color } : note
    );
    
    const newDrawingState = {
      ...drawingState,
      lines: newLines,
      textNotes: newTextNotes
    };
    
    handleUpdateLines(newDrawingState.lines, newDrawingState.textNotes);
    setUiState(prev => ({ ...prev, penColor: color }));
  };
  
  const handleLineWidthChange = (width: number) => {
    const newLines = drawingState.lines.map(line => 
      uiState.selection.includes(line.id) ? { ...line, width } : line
    );
    
    const newTextNotes = (drawingState.textNotes || []).map(note => 
      uiState.selection.includes(note.id) ? { ...note, fontSize: width * 3 } : note
    );
    
    const newDrawingState = {
      ...drawingState,
      lines: newLines,
      textNotes: newTextNotes
    };
    
    handleUpdateLines(newDrawingState.lines, newDrawingState.textNotes);
    setUiState(prev => ({ ...prev, penWidth: width }));
  };
  
  const handleUndo = () => {
    if (historyManagerRef.current) {
      const newState = historyManagerRef.current.undo();
      if (newState) {
        setDrawingState(newState.present);
        setCanUndo(historyManagerRef.current.canUndo());
        setCanRedo(historyManagerRef.current.canRedo());
      }
    }
  };
  
  const handleRedo = () => {
    if (historyManagerRef.current) {
      const newState = historyManagerRef.current.redo();
      if (newState) {
        setDrawingState(newState.present);
        setCanUndo(historyManagerRef.current.canUndo());
        setCanRedo(historyManagerRef.current.canRedo());
      }
    }
  };
  
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '1') {
        e.preventDefault();
        setUiState(prev => ({ ...prev, tool: 'pointer' }));
      } else if (e.key === '2') {
        e.preventDefault();
        setUiState(prev => ({ ...prev, tool: 'hand' }));
      } else if (e.key === '3') {
        e.preventDefault();
        setUiState(prev => ({ ...prev, tool: 'eraser' }));
      } else if (e.key === '4') {
        e.preventDefault();
        setUiState(prev => ({ ...prev, tool: 'pen' }));
      } else if (e.key === '5') {
        e.preventDefault();
        setUiState(prev => ({ ...prev, tool: 'line' }));
      } else if (e.key === '6') {
        e.preventDefault();
        setUiState(prev => ({ ...prev, tool: 'text' }));
      }
      
      const isCtrlOrCmd = e.ctrlKey || e.metaKey;
      
      if (isCtrlOrCmd) {
        if (e.key.toLowerCase() === 'z' && !e.shiftKey) {
          e.preventDefault();
          handleUndo();
        } else if (e.key.toLowerCase() === 'y') {
          e.preventDefault();
          handleRedo();
        } else if (e.key.toLowerCase() === 'z' && e.shiftKey) {
          e.preventDefault();
          handleRedo();
        }
      }
      
      if (e.key === 'Delete' && uiState.tool === 'pointer' && uiState.selection.length > 0) {
        e.preventDefault();
        const newLines = drawingState.lines.filter(
          line => !uiState.selection.includes(line.id)
        );
        const newTextNotes = (drawingState.textNotes || []).filter(
          note => !uiState.selection.includes(note.id)
        );
        
        handleUpdateLines(newLines, newTextNotes);
        setUiState(prev => ({ ...prev, selection: [] }));
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [uiState, drawingState]);
  
  const isDarkTheme = backgroundType === 'black';
  
  return (
    <div className={`app ${isDarkTheme ? 'dark-theme' : 'light-theme'}`}>
      <header className={`app-header ${isDarkTheme ? 'dark-theme' : 'light-theme'}`}>
        <Toolbar
          currentTool={uiState.tool}
          onToolChange={handleToolChange}
          penColor={uiState.penColor}
          onColorChange={handleColorChange}
          penWidth={uiState.penWidth}
          onWidthChange={handleLineWidthChange}
          lineStyle={uiState.lineStyle}
          onLineStyleChange={handleLineStyleChange}
          onLineStylesChange={handleLineStylesChange}
          backgroundType={backgroundType}
          onBackgroundChange={handleBackgroundChange}
          canUndo={canUndo}
          canRedo={canRedo}
          onUndo={handleUndo}
          onRedo={handleRedo}
          selectedLines={uiState.selection}
          onLineColorChange={handleLineColorChange}
          onLineWidthChange={handleLineWidthChange}
        />
      </header>
      
      <main className="canvas-area">
        <CanvasComponent 
          backgroundType={backgroundType}
          drawingState={drawingState}
          uiState={uiState}
          onUiStateChange={handleUiStateChange}
          onAddLine={handleAddLine}
          onDeleteLine={handleDeleteLine}
          onUpdateLines={handleUpdateLines}
        />
      </main>
    </div>
  );
}

export default App;